import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { AuthenticationModule } from './authentication/authentication.module';
import { EnsureModuleLoadedOnceGuard } from './ensure-module-loaded-once-guard';
import { HttpErrorHandler, HttpUtility } from './http';

import {
  API_BASE_HH_URL,
  API_BASE_NOTIFICATIONS,
  API_BASE_PLATFORM,
  API_BASE_REGISTRY_URL,
  API_BASE_URL,
  apiUrlFactory,
  AUTH_CONFIG,
  B2C_POLICIES,
  BASE_PERMISSIONS_URL,
  CONFIG_FILE,
  ConfigFactoryAuthConfig,
  ConfigFactoryB2CPolicies,
  ConfigFactoryBasePermissionsUrl,
  ConfigService,
  WCC_LIBRARY_URL,
  API_BASE_SPECIALTY_PROGRAMS_URL
} from './config/config.service';

export function generateHashKey(): string {
  return Math.random().toString(16);
}

@NgModule({
  imports: [CommonModule, AuthenticationModule],
  providers: [
    ConfigService,
    {
      provide: CONFIG_FILE,
      useValue: `../config.json?v=${generateHashKey()}`
    },
    {
      provide: BASE_PERMISSIONS_URL,
      useFactory: ConfigFactoryBasePermissionsUrl,
      deps: [API_BASE_URL]
    },
    {
      provide: API_BASE_URL,
      useFactory: (...args: [ConfigService, string]) => apiUrlFactory(...args).apiUrl,
      deps: [ConfigService, CONFIG_FILE]
    },
    {
      provide: API_BASE_HH_URL,
      useFactory: (...args: [ConfigService, string]) => apiUrlFactory(...args).hhApiUrl,
      deps: [ConfigService, CONFIG_FILE]
    },
    {
      provide: API_BASE_PLATFORM,
      useFactory: (...args: [ConfigService, string]) => apiUrlFactory(...args).platformUrl,
      deps: [ConfigService, CONFIG_FILE]
    },
    {
      provide: API_BASE_REGISTRY_URL,
      useFactory: (...args: [ConfigService, string]) => apiUrlFactory(...args).registryUrl,
      deps: [ConfigService, CONFIG_FILE]
    },
    {
      provide: WCC_LIBRARY_URL,
      useFactory: (...args: [ConfigService, string]) => apiUrlFactory(...args).wccLibraryUrl,
      deps: [ConfigService, CONFIG_FILE]
    },    
    {
      provide: API_BASE_SPECIALTY_PROGRAMS_URL,
      useFactory: (...args: [ConfigService, string]) => apiUrlFactory(...args).specialtyProgramsUrl,
      deps: [ConfigService, CONFIG_FILE]
    },
    {
      provide: API_BASE_NOTIFICATIONS,
      useFactory: (...args: [ConfigService, string]) =>
        apiUrlFactory(...args).notificationsGatewayUrl,
      deps: [ConfigService, CONFIG_FILE]
    },
    {
      provide: AUTH_CONFIG,
      useFactory: ConfigFactoryAuthConfig,
      deps: [ConfigService, CONFIG_FILE]
    },
    {
      provide: B2C_POLICIES,
      useFactory: ConfigFactoryB2CPolicies,
      deps: [ConfigService, CONFIG_FILE]
    },
    { provide: 'windowRef', useValue: window },
    { provide: 'documentRef', useValue: document },
    MessageService,
    HttpErrorHandler,
    HttpUtility,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class CoreModule extends EnsureModuleLoadedOnceGuard {
  // Ensure that CoreModule is only loaded into AppModule
  constructor() {
    const parentModule = inject(CoreModule, { optional: true, skipSelf: true })!;

    super(parentModule);
  }
}
